import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/LoadingSpinner';
import { publicQuoteAPI } from '../../services/api';
import TripPlanSummary from '../../components/reservations/TripPlanSummary';

function formatMoney(value?: string | number | null) {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number.isFinite(amount) ? amount : 0);
}

function formatDate(value?: string | null) {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'N/A'
    : date.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
}

function formatShortDate(value?: string | null) {
  if (!value) return 'Date TBD';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Date TBD'
    : date.toLocaleDateString([], {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
}

function formatTime(value?: string | null) {
  if (!value) return 'Time TBD';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Time TBD'
    : date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      });
}

function labelize(value?: string | null) {
  return (value || 'N/A')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function customerStatusLabel(status?: string | null) {
  switch ((status || '').toLowerCase()) {
    case 'draft':
    case 'sent':
      return 'Ready to review';
    case 'accepted':
      return 'Accepted';
    case 'declined':
      return 'Declined';
    case 'converted':
      return 'Confirmed';
    case 'expired':
      return 'Expired';
    default:
      return labelize(status);
  }
}

function customerLifecycleLabel(quote: Record<string, any>) {
  const status = String(quote.status || '').toLowerCase();
  const paymentRequired = Boolean(quote.paymentRequired);
  const depositPaymentStatus = String(
    quote.depositPayment?.status || quote.depositPaymentStatus || ''
  ).toLowerCase();
  const hasDepositLink = Boolean(
    quote.depositPaymentUrl || quote.tripDetails?.deposit_payment_url
  );

  if (paymentRequired && depositPaymentStatus === 'paid') {
    return 'Deposit paid';
  }

  if (
    paymentRequired &&
    hasDepositLink &&
    ['accepted', 'converted'].includes(status)
  ) {
    return 'Deposit due';
  }

  return customerStatusLabel(status);
}

function formatAddress(value?: Record<string, any> | null, fallback = 'TBD') {
  if (!value || typeof value !== 'object') return fallback;
  const formatted = value.formatted || value.formatted_location;
  if (formatted) return String(formatted);

  return (
    [
      value.name,
      value.line1 || value.address_line1,
      value.line2 || value.address_line2,
      [value.city, value.state, value.postalCode || value.postal_code]
        .filter(Boolean)
        .join(', '),
    ]
      .filter(Boolean)
      .join(' · ') || fallback
  );
}

function getStopAddress(stop: Record<string, any>) {
  return formatAddress(
    stop.address || stop,
    stop.formatted_location || stop.location || 'TBD'
  );
}

function cleanVehicleLabel(requirement: Record<string, any>) {
  const rawLabel = String(
    requirement.vehicle_label || requirement.vehicleLabel || ''
  )
    .replace(/\s*[-–—]?\s*mirrored\s+from\s+invoice\s+line\s*/gi, '')
    .replace(/\s*[-–—]?\s*mirror\s+of\s+invoice\s+line\s*/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

  return rawLabel || `${labelize(requirement.vehicle_type)} service`;
}

function getBillableLabel(requirement: Record<string, any>) {
  const rateType = requirement.rate_type || requirement.rateType || 'fixed';
  const units = Number(requirement.billable_units || 0);
  if (rateType === 'fixed') return 'Quoted package price';
  if (!Number.isFinite(units) || units <= 0) return labelize(rateType);
  if (rateType === 'hourly') return `${units} scheduled hours included`;
  if (rateType === 'daily') return `${units} service days included`;
  return `${units} ${units === 1 ? 'service unit' : 'service units'} included`;
}

function getAddressNote(value?: Record<string, any> | null) {
  if (!value || typeof value !== 'object') return null;
  return typeof value.passenger_instructions === 'string' ? value.passenger_instructions : null;
}

function SectionHeader({
  eyebrow,
  title,
  description,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
}) {
  return (
    <div>
      {eyebrow ? (
        <p className="text-xs font-semibold uppercase text-on-surface-variant">
          {eyebrow}
        </p>
      ) : null}
      <h2 className="mt-1 text-xl font-semibold text-on-surface">{title}</h2>
      {description ? (
        <p className="mt-2 text-sm leading-6 text-on-surface-variant">
          {description}
        </p>
      ) : null}
    </div>
  );
}

function CustomerTermsCards({ terms }: { terms?: Record<string, any> | null }) {
  const deposit = terms?.deposit || {};
  const firstCancellationRule = Array.isArray(terms?.cancellation_policy?.rules)
    ? terms?.cancellation_policy?.rules[0]
    : null;
  const depositLabel =
    deposit.type === 'none'
      ? 'No deposit required'
      : deposit.type === 'fixed'
      ? `${formatMoney(deposit.value)} due to confirm`
      : `${Number(deposit.value || 0).toFixed(0)}% due to confirm`;
  const paymentLabel =
    terms?.payment_terms === 'due_on_receipt'
      ? 'Balance due on receipt'
      : labelize(terms?.payment_terms);
  const cancellationLabel = firstCancellationRule
    ? `${firstCancellationRule.refund_percent}% refund until ${firstCancellationRule.hours_before_pickup} hours before pickup`
    : 'Cancellation terms apply after acceptance';

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {[
        ['Deposit', depositLabel],
        ['Balance', paymentLabel],
        [
          'Quote window',
          `${Number(terms?.quote_expiration_hours || 0).toFixed(
            0
          )} hours to accept`,
        ],
        ['Cancellation', cancellationLabel],
      ].map(([label, value]) => (
        <div
          key={label}
          className="rounded-card border border-outline/60 bg-surface-container-low p-4"
        >
          <p className="text-xs font-semibold uppercase text-on-surface-variant">
            {label}
          </p>
          <p className="mt-2 font-semibold leading-6 text-on-surface">
            {value}
          </p>
        </div>
      ))}
    </div>
  );
}

export default function PublicQuotePage() {
  const { token } = useParams<{ token: string }>();
  const [declineReason, setDeclineReason] = useState('');
	  const [acceptedPaymentUrl, setAcceptedPaymentUrl] = useState<string | null>(
	    null
	  );
	  const [acceptedReservationUrl, setAcceptedReservationUrl] = useState<
	    string | null
	  >(null);

  const {
    data: quote,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ['public-quote', token],
    async () => {
      const response = await publicQuoteAPI.getQuote(token || '');
      return response.data;
    },
    { enabled: Boolean(token), retry: false }
  );

  const acceptMutation = useMutation(
    async () =>
      publicQuoteAPI.acceptQuote(token || '', {
        baseUrl:
          typeof window !== 'undefined' ? window.location.origin : undefined,
      }),
    {
	      onSuccess: async (response) => {
	        const paymentUrl =
	          response?.data?.paymentUrl ||
	          response?.data?.quote?.depositPaymentUrl ||
	          null;
	        const reservationUrl =
	          response?.data?.reservationPublicUrl ||
	          response?.data?.quote?.convertedReservationPublicUrl ||
	          null;
	        if (reservationUrl) {
	          setAcceptedReservationUrl(reservationUrl);
	        }
	        if (paymentUrl) {
	          setAcceptedPaymentUrl(paymentUrl);
	          toast.success('Quote accepted. Opening deposit payment...');
          window.location.href = paymentUrl;
          return;
        }
        if (response?.data?.paymentError) {
          toast.error(response.data.paymentError);
        } else {
          toast.success('Quote accepted');
        }
        await refetch();
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || 'Unable to accept quote');
      },
    }
  );

  const declineMutation = useMutation(
    async () =>
      publicQuoteAPI.declineQuote(token || '', {
        reason: declineReason || undefined,
      }),
    {
      onSuccess: async () => {
        toast.success('Quote declined');
        await refetch();
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || 'Unable to decline quote');
      },
    }
  );

  const requirements = useMemo(
    () =>
      Array.isArray(quote?.vehicleRequirements)
        ? quote.vehicleRequirements
        : [],
    [quote?.vehicleRequirements]
  );
  const stops = useMemo(
    () =>
      Array.isArray(quote?.tripDetails?.stops) ? quote.tripDetails.stops : [],
    [quote?.tripDetails?.stops]
  );

  if (isLoading) {
    return (
      <main className="min-h-screen bg-surface px-4 py-20">
        <LoadingSpinner className="py-20" />
      </main>
    );
  }

  if (error || !quote) {
    return (
      <main className="min-h-screen bg-surface px-4 py-20">
        <div className="mx-auto max-w-2xl rounded-card border border-outline/60 bg-surface-container-lowest p-8 text-center">
          <h1 className="text-2xl font-semibold text-on-surface">
            Quote unavailable
          </h1>
          <p className="mt-2 text-on-surface-variant">
            {(error as any)?.response?.data?.message ||
              'This quote link is invalid or expired.'}
          </p>
        </div>
      </main>
    );
  }

  const company = quote.company || {};
  const companyName = company.appName || company.name || 'ModalOS';
  const primaryColor = company.primaryColor || '#2563eb';
  const startDate =
    quote.tripDetails?.startDate || quote.tripDetails?.start_date;
  const endDate = quote.tripDetails?.endDate || quote.tripDetails?.end_date;
  const pickupAddress = quote.tripDetails?.pickup_address;
  const dropoffAddress = quote.tripDetails?.dropoff_address;
  const depositPaymentStatus = String(
    quote.depositPayment?.status || quote.depositPaymentStatus || ''
  ).toLowerCase();
  const depositPaidAt =
    quote.depositPayment?.paidAt || quote.depositPaymentPaidAt || null;
  const isDepositPaid = depositPaymentStatus === 'paid';
  const acceptLabel = acceptMutation.isLoading
    ? 'Accepting...'
    : quote.paymentRequired
    ? 'Accept and Pay Deposit'
    : 'Accept Quote';
	  const depositPaymentUrl =
	    acceptedPaymentUrl ||
	    quote.depositPaymentUrl ||
	    quote.tripDetails?.deposit_payment_url ||
	    null;
	  const reservationPublicUrl =
	    acceptedReservationUrl ||
	    quote.convertedReservationPublicUrl ||
	    quote.tripDetails?.converted_reservation_public_url ||
	    null;
  const quoteStatus = String(quote.status || '').toLowerCase();
  const showDepositPaymentAction = Boolean(
    !quote.canAccept &&
      quote.paymentRequired &&
      depositPaymentUrl &&
      !isDepositPaid &&
      ['accepted', 'converted'].includes(quoteStatus)
  );
  const showDepositReceivedState = Boolean(
    quote.paymentRequired &&
      isDepositPaid &&
      ['accepted', 'converted'].includes(quoteStatus)
  );
  const isAcceptedOrConverted = ['accepted', 'converted'].includes(quoteStatus);
	  const openDepositPayment = () => {
	    if (depositPaymentUrl) {
	      window.location.href = depositPaymentUrl;
	    }
	  };
	  const openReservationRecord = () => {
	    if (reservationPublicUrl) {
	      window.location.href = reservationPublicUrl;
	    }
	  };

  return (
    <main className="min-h-screen bg-surface pb-28 lg:pb-0">
      <header className="bg-[#101724] text-white">
        <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-5">
            <div className="flex min-w-0 items-center gap-4">
              {company.logoUrl ? (
                <img
                  src={company.logoUrl}
                  alt={companyName}
                  className="h-16 w-auto max-w-[15rem] rounded-md object-contain"
                />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-md border border-white/20 bg-white/10 text-lg font-semibold">
                  {companyName.slice(0, 1)}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm text-white/70">
                  Your private charter quote
                </p>
                <h1 className="text-xl font-semibold leading-tight sm:text-2xl">
                  {companyName}
                </h1>
              </div>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-sm text-white/70">{quote.quoteNumber}</p>
              <span className="mt-2 inline-flex rounded-pill border border-white/20 bg-white/10 px-3 py-1 text-sm font-medium">
                {customerLifecycleLabel(quote)}
              </span>
            </div>
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
            <div>
              <p className="text-sm font-medium uppercase text-white/60">
                Prepared for your review
              </p>
              <h2 className="mt-2 text-3xl font-semibold">
                {quote.customerName || quote.customerEmail || 'Guest'}
              </h2>
              {quote.customerEmail ? (
                <p className="mt-2 text-white/70">{quote.customerEmail}</p>
              ) : null}
              <p className="mt-5 max-w-2xl text-base leading-7 text-white/75">
                {showDepositReceivedState
                  ? 'Your deposit has been received. The transportation team has the itinerary, vehicle requirements, and terms below for final confirmation.'
                  : isAcceptedOrConverted
                  ? 'Your quote has been accepted. Complete the deposit to secure the requested transportation.'
                  : 'Please review the itinerary, vehicle, price, and terms below. When everything looks right, accept the quote to reserve the requested transportation.'}
              </p>
            </div>
            <div className="rounded-card border border-white/15 bg-white/10 p-4">
              <p className="text-sm text-white/70">Quote total</p>
              <p className="mt-1 text-3xl font-semibold">
                {formatMoney(quote.total)}
              </p>
              <p className="mt-3 text-sm text-white/70">
                {showDepositReceivedState
                  ? 'Deposit received: '
                  : 'Due today to reserve: '}
                <span className="font-semibold text-white">
                  {formatMoney(quote.depositDue)}
                </span>
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-6xl gap-6 px-4 py-8 sm:px-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <section className="space-y-6">
          <div className="rounded-card border border-outline/60 bg-surface-container-lowest p-5">
            <SectionHeader
              eyebrow="Your trip"
              title={quote.eventName || labelize(quote.eventType)}
              description={`Service is scheduled for ${formatShortDate(
                quote.eventDate
              )}. Please confirm timing and routing before accepting.`}
            />
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              <div className="rounded-card bg-surface-container-low p-3">
                <p className="text-xs font-semibold uppercase text-on-surface-variant">
                  Date
                </p>
                <p className="mt-1 font-semibold text-on-surface">
                  {formatShortDate(quote.eventDate)}
                </p>
              </div>
              <div className="rounded-card bg-surface-container-low p-3">
                <p className="text-xs font-semibold uppercase text-on-surface-variant">
                  Start
                </p>
                <p className="mt-1 font-semibold text-on-surface">
                  {formatTime(startDate)}
                </p>
              </div>
              <div className="rounded-card bg-surface-container-low p-3">
                <p className="text-xs font-semibold uppercase text-on-surface-variant">
                  End
                </p>
                <p className="mt-1 font-semibold text-on-surface">
                  {formatTime(endDate)}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-card border border-outline/60 bg-surface-container-lowest p-5">
            <SectionHeader
              eyebrow="Itinerary"
              title="Pickup, Stops, and Dropoff"
              description="These are the locations and notes the transportation team will use for your service."
            />
            <div className="mt-5 divide-y divide-outline/60">
              {[
                {
                  label: 'Pickup',
                  address: formatAddress(
                    pickupAddress,
                    quote.tripDetails?.pickup_location || 'TBD'
                  ),
                  note: getAddressNote(pickupAddress),
                },
                ...stops.map((stop: any, index: number) => ({
                  label: `Stop ${index + 1}`,
                  address: getStopAddress(stop),
                  note: getAddressNote(stop.address || stop),
                })),
                {
                  label: 'Dropoff',
                  address: formatAddress(
                    dropoffAddress,
                    quote.tripDetails?.dropoff_location || 'TBD'
                  ),
                  note: getAddressNote(dropoffAddress),
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="grid gap-3 py-4 first:pt-0 last:pb-0 sm:grid-cols-[120px_minmax(0,1fr)]"
                >
                  <div>
                    <p className="text-sm font-semibold text-on-surface">
                      {item.label}
                    </p>
                    <span
                      className="mt-2 hidden h-2 w-2 rounded-full sm:block"
                      style={{ backgroundColor: primaryColor }}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-on-surface">
                      {item.address}
                    </p>
                    {item.note ? (
                      <p className="mt-1 text-sm text-on-surface-variant">
                        {item.note}
                      </p>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-card border border-outline/60 bg-surface-container-lowest p-5">
            <SectionHeader
              eyebrow="Vehicle"
              title="Transportation Included"
              description="This quote includes the vehicle type and passenger capacity needed for the trip."
            />
            <div className="mt-5 divide-y divide-outline/60">
              {requirements.map((requirement: any) => (
                <div
                  key={
                    requirement.id ||
                    `${requirement.vehicle_type}-${requirement.quantity}`
                  }
                  className="flex flex-wrap items-start justify-between gap-4 py-4 first:pt-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <p className="text-base font-semibold text-on-surface">
                      {requirement.quantity} {cleanVehicleLabel(requirement)}
                    </p>
                    <p className="mt-1 text-sm text-on-surface-variant">
                      {getBillableLabel(requirement)}
                      {requirement.passenger_capacity_required
                        ? ` · Comfortable seating for up to ${requirement.passenger_capacity_required} passengers`
                        : ''}
                    </p>
                  </div>
                  <p className="font-semibold text-on-surface">
                    {formatMoney(requirement.subtotal)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-card border border-outline/60 bg-surface-container-lowest p-5">
            <SectionHeader
              eyebrow="Terms"
              title="Before You Accept"
              description="A deposit reserves the quoted transportation. Final balance and cancellation terms are shown below."
            />
            <div className="mt-5">
              <TripPlanSummary value={quote.tripDetails || {}} />
              <CustomerTermsCards terms={quote.termsSnapshot} />
            </div>
            {quote.termsSnapshot?.terms_text ? (
              <p className="mt-5 whitespace-pre-wrap text-sm leading-6 text-on-surface-variant">
                {quote.termsSnapshot.terms_text}
              </p>
            ) : null}
          </div>
        </section>

        <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
          <div className="rounded-card border border-outline/60 bg-surface-container-lowest p-5 shadow-ambient">
            <h3 className="text-base font-semibold text-on-surface">
              Price Summary
            </h3>
            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-on-surface-variant">Subtotal</dt>
                <dd>{formatMoney(quote.subtotal)}</dd>
              </div>
              {Number(quote.serviceChargeAmount || 0) > 0 ? (
                <div className="flex justify-between gap-3">
                  <dt className="text-on-surface-variant">Service charge</dt>
                  <dd>{formatMoney(quote.serviceChargeAmount)}</dd>
                </div>
              ) : null}
              {Number(quote.gratuityAmount || 0) > 0 ? (
                <div className="flex justify-between gap-3">
                  <dt className="text-on-surface-variant">Gratuity</dt>
                  <dd>{formatMoney(quote.gratuityAmount)}</dd>
                </div>
              ) : null}
              {Number(quote.taxAmount || 0) > 0 ? (
                <div className="flex justify-between gap-3">
                  <dt className="text-on-surface-variant">Tax</dt>
                  <dd>{formatMoney(quote.taxAmount)}</dd>
                </div>
              ) : null}
              <div className="flex justify-between gap-3 border-t border-outline/60 pt-3 text-lg font-semibold">
                <dt>Total</dt>
                <dd>{formatMoney(quote.total)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-on-surface-variant">
                  {showDepositReceivedState ? 'Deposit paid' : 'Deposit due'}
                </dt>
                <dd>{formatMoney(quote.depositDue)}</dd>
              </div>
            </dl>
            {showDepositReceivedState ? (
              <p className="mt-4 rounded-card border border-success/30 bg-success-container/20 p-3 text-xs font-medium text-success-on-container">
                Deposit received
                {depositPaidAt ? ` on ${formatDate(depositPaidAt)}` : ''}. The
                reservation is ready for final operations review.
              </p>
            ) : null}
            <p className="mt-4 text-xs text-on-surface-variant">
              Expires {formatDate(quote.expiresAt)}
            </p>
          </div>

          {quote.canAccept ? (
            <div className="hidden rounded-card border border-outline/60 bg-surface-container-lowest p-5 shadow-ambient lg:block">
              <button
                type="button"
                className="w-full rounded-card px-4 py-3 text-sm font-semibold text-white transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
                style={{ backgroundColor: primaryColor }}
                disabled={acceptMutation.isLoading}
                onClick={() => acceptMutation.mutate()}
              >
                {acceptLabel}
              </button>
              {quote.canDecline ? (
                <div className="mt-4 space-y-2">
                  <textarea
                    className="input-field min-h-[88px]"
                    placeholder="Optional decline note"
                    value={declineReason}
                    onChange={(event) => setDeclineReason(event.target.value)}
                  />
                  <button
                    type="button"
                    className="btn-secondary w-full"
                    disabled={declineMutation.isLoading}
                    onClick={() => declineMutation.mutate()}
                  >
                    {declineMutation.isLoading
                      ? 'Declining...'
                      : 'Decline Quote'}
                  </button>
                </div>
              ) : null}
            </div>
          ) : showDepositPaymentAction ? (
            <div className="hidden rounded-card border border-outline/60 bg-surface-container-lowest p-5 shadow-ambient lg:block">
              <p className="text-sm font-semibold text-on-surface">
                Deposit due
              </p>
              <p className="mt-2 text-sm leading-6 text-on-surface-variant">
                Your transportation has moved into confirmation. Complete the
                deposit to secure the reservation.
              </p>
              <button
                type="button"
                className="mt-4 w-full rounded-card px-4 py-3 text-sm font-semibold text-white transition hover:brightness-95"
                style={{ backgroundColor: primaryColor }}
                onClick={openDepositPayment}
              >
                Pay Deposit {formatMoney(quote.depositDue)}
              </button>
            </div>
	          ) : showDepositReceivedState ? (
	            <div className="rounded-card border border-success/30 bg-success-container/20 p-5 text-sm text-success-on-container shadow-ambient">
	              <p className="font-semibold">Deposit received</p>
	              <p className="mt-2 leading-6">
	                Thanks. Your deposit is recorded and the reservation team can
	                continue final coordination.
	              </p>
	              {depositPaidAt ? (
	                <p className="mt-3 text-xs">Paid {formatDate(depositPaidAt)}</p>
	              ) : null}
	              {reservationPublicUrl ? (
	                <button
	                  type="button"
	                  className="mt-4 w-full rounded-card px-4 py-3 text-sm font-semibold text-white transition hover:brightness-95"
	                  style={{ backgroundColor: primaryColor }}
	                  onClick={openReservationRecord}
	                >
	                  View Reservation Record
	                </button>
	              ) : null}
	            </div>
	          ) : reservationPublicUrl ? (
	            <div className="hidden rounded-card border border-outline/60 bg-surface-container-lowest p-5 shadow-ambient lg:block">
	              <p className="text-sm font-semibold text-on-surface">
	                Reservation record
	              </p>
	              <p className="mt-2 text-sm leading-6 text-on-surface-variant">
	                Your quote has been accepted and your reservation record is
	                available for your files.
	              </p>
	              <button
	                type="button"
	                className="mt-4 w-full rounded-card px-4 py-3 text-sm font-semibold text-white transition hover:brightness-95"
	                style={{ backgroundColor: primaryColor }}
	                onClick={openReservationRecord}
	              >
	                View Reservation Record
	              </button>
	            </div>
	          ) : (
            <div className="rounded-card border border-outline/60 bg-surface-container-lowest p-5 text-sm text-on-surface-variant">
              This quote is no longer available for online acceptance.
            </div>
          )}

          {company.supportEmail ? (
            <p className="text-sm leading-6 text-on-surface-variant">
              Questions? Contact{' '}
              <a
                className="font-medium underline"
                href={`mailto:${company.supportEmail}`}
              >
                {company.supportEmail}
              </a>
              .
            </p>
          ) : null}
        </aside>
      </div>

      {quote.canAccept ? (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-outline/70 bg-surface-container-lowest/95 px-4 py-3 shadow-float backdrop-blur lg:hidden">
          <div className="mx-auto flex max-w-lg items-center gap-3">
            {quote.canDecline ? (
              <button
                type="button"
                className="btn-secondary flex-1 px-3 py-3 text-sm"
                disabled={declineMutation.isLoading}
                onClick={() => declineMutation.mutate()}
              >
                {declineMutation.isLoading ? 'Declining...' : 'Decline'}
              </button>
            ) : null}
            <button
              type="button"
              className="flex-[1.5] rounded-card px-4 py-3 text-sm font-semibold text-white transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
              style={{ backgroundColor: primaryColor }}
              disabled={acceptMutation.isLoading}
              onClick={() => acceptMutation.mutate()}
            >
              {acceptLabel}
            </button>
          </div>
        </div>
	      ) : showDepositPaymentAction ? (
	        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-outline/70 bg-surface-container-lowest/95 px-4 py-3 shadow-float backdrop-blur lg:hidden">
          <div className="mx-auto flex max-w-lg items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-on-surface-variant">Deposit due</p>
              <p className="font-semibold text-on-surface">
                {formatMoney(quote.depositDue)}
              </p>
            </div>
            <button
              type="button"
              className="rounded-card px-4 py-3 text-sm font-semibold text-white transition hover:brightness-95"
              style={{ backgroundColor: primaryColor }}
              onClick={openDepositPayment}
            >
              Pay Deposit
	            </button>
	          </div>
	        </div>
	      ) : reservationPublicUrl ? (
	        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-outline/70 bg-surface-container-lowest/95 px-4 py-3 shadow-float backdrop-blur lg:hidden">
	          <div className="mx-auto flex max-w-lg items-center gap-3">
	            <div className="min-w-0 flex-1">
	              <p className="text-xs text-on-surface-variant">Accepted</p>
	              <p className="font-semibold text-on-surface">
	                Reservation record ready
	              </p>
	            </div>
	            <button
	              type="button"
	              className="rounded-card px-4 py-3 text-sm font-semibold text-white transition hover:brightness-95"
	              style={{ backgroundColor: primaryColor }}
	              onClick={openReservationRecord}
	            >
	              View Record
	            </button>
	          </div>
	        </div>
	      ) : null}
    </main>
  );
}
